import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../assets/Generallogo.png';
import { FaHome, FaInfoCircle, FaLayerGroup, FaTools, FaEnvelope, FaBars, FaTimes, FaUserCircle, FaHistory, FaCube } from 'react-icons/fa';

const MyNavbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [activeLink, setActiveLink] = useState('home');
    const location = useLocation();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };

        // Initial user check
        setUser(localStorage.getItem('user'));

        window.addEventListener('scroll', handleScroll);
        // Add storage event listener to update user status across tabs
        window.addEventListener('storage', () => setUser(localStorage.getItem('user')));

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('storage', () => setUser(localStorage.getItem('user')));
        };
    }, []);

    // Also check on component re-render (since storage event doesn't fire in the same tab)
    useEffect(() => {
        const interval = setInterval(() => {
            const currentStatus = localStorage.getItem('user');
            if (currentStatus !== user) setUser(currentStatus);
        }, 1000);
        return () => clearInterval(interval);
    }, [user]);

    const toggleSidebar = () => setIsOpen(!isOpen);
    const closeSidebar = () => setIsOpen(false);

    const handleNavClick = (id) => {
        closeSidebar();

        if (location.pathname !== '/') {
            // Navigate to home first, then scroll will be handled by Home component
            navigate('/', { state: { scrollTo: id } });
            return;
        }

        setActiveLink(id);
        const element = document.getElementById(id);
        if (element) {
            const offset = 80; // Account for fixed navbar
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = element.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        } else if (id === 'home') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const navItems = [
        { id: 'home', label: 'Home', icon: <FaHome /> },
        { id: 'about', label: 'About', icon: <FaInfoCircle /> },
        { id: 'components', label: 'Components', icon: <FaLayerGroup /> },
        { id: 'service', label: 'Service', icon: <FaTools /> },
        { id: 'products', label: 'Products', icon: <FaCube /> },
        { id: 'contact', label: 'Contact', icon: <FaEnvelope /> },
    ];

    if (user) {
        navItems.push({ id: 'history', label: 'Bookings', icon: <FaHistory />, link: '/booking-history' });
    }

    return (
        <>
            <nav className={`navbar-professional ${scrolled ? 'scrolled' : ''}`}>
                <div className="container-fluid d-flex align-items-center justify-content-between px-3 px-md-5">
                    <Link to="/" className="navbar-brand p-0 me-3 d-flex flex-column align-items-center text-center" onClick={() => handleNavClick('home')}>
                        <span className="navbar-logo-text leading-none" style={{ 
                            fontWeight: '700', 
                            color: '#fff', 
                            letterSpacing: '2px',
                            fontSize: 'clamp(1rem, 4vw, 1.2rem)',
                            textTransform: 'uppercase',
                            fontFamily: 'Outfit, sans-serif',
                            whiteSpace: 'nowrap',
                            lineHeight: '1'
                        }}>
                            AL WASEEQ DESIGN
                        </span>
                        <span className="navbar-tagline" style={{
                            fontSize: '0.6rem',
                            color: '#fff',
                            letterSpacing: '2px',
                            fontWeight: '500',
                            textTransform: 'uppercase',
                            marginTop: '2px',
                            opacity: '0.8'
                        }}>
                            We Craft Your Dream World
                        </span>
                    </Link>

                    {/* Desktop Menu */}
                    <div className="d-none d-lg-flex align-items-center">
                        {navItems.map((item) => (
                            <Link
                                key={item.id}
                                to={item.link ? item.link : "/"}
                                className={`nav-link-prof ${activeLink === item.id ? 'active' : ''}`}
                                onClick={(e) => {
                                    if (item.id !== 'history') {
                                        e.preventDefault();
                                        handleNavClick(item.id);
                                    } else {
                                        setActiveLink('history');
                                        closeSidebar();
                                    }
                                }}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </div>

                    {/* Mobile Toggler & Profile */}
                    <div className="d-lg-none d-flex align-items-center gap-3">
                        {user ? (
                            <Link to="/profile" className="text-secondary" style={{ fontSize: '1.5rem' }}>
                                <FaUserCircle />
                            </Link>
                        ) : (
                            <Link to="/login" className="btn-gold px-3 py-2" style={{ fontSize: '0.6rem', letterSpacing: '1px' }}>
                                LOGIN
                            </Link>
                        )}
                        <button className="border-0 bg-transparent" onClick={toggleSidebar}>
                            <FaBars style={{ color: 'var(--secondary-color)', fontSize: '1.5rem' }} />
                        </button>
                    </div>

                    {/* Desktop Profile Icon / Login Button */}
                    <div className="d-none d-lg-block ms-4">
                        {user ? (
                            <Link to="/profile" className="nav-profile-icon" title="Profile">
                                <FaUserCircle />
                            </Link>
                        ) : (
                            <Link to="/login" className="btn-gold px-4 py-2" style={{ fontSize: '0.7rem' }}>
                                LOGIN
                            </Link>
                        )}
                    </div>
                </div>
            </nav>

            {/* Mobile Sidebar */}
            <div className={`sidebar-overlay ${isOpen ? 'open' : ''}`} onClick={closeSidebar}></div>
            <div className={`sidebar-panel shadow-lg ${isOpen ? 'open' : ''}`}>
                <div className="d-flex justify-content-between align-items-center mb-5">
                    <span className="section-index">MENU</span>
                    <button className="bg-transparent border-0" onClick={closeSidebar}>
                        <FaTimes style={{ color: 'var(--secondary-color)', fontSize: '1.2rem' }} />
                    </button>
                </div>
                <ul className="list-unstyled">
                    {navItems.map((item) => (
                        <li key={item.id} className="mb-4">
                            <Link
                                to={item.link ? item.link : "/"}
                                className={`nav-link-prof ${activeLink === item.id ? 'active' : ''}`}
                                style={{ fontSize: '1.2rem', marginLeft: 0 }}
                                onClick={(e) => {
                                    if (item.id !== 'history') {
                                        e.preventDefault();
                                        handleNavClick(item.id);
                                    } else {
                                        setActiveLink('history');
                                        closeSidebar();
                                    }
                                }}
                            >
                                <span className="me-3">{item.icon}</span>
                                {item.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
        </>
    );
};

export default MyNavbar;

